import { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { chartApi, type ChartDataPoint } from '../../lib/api-client';
import { calculateTrendline, calculateLinearTrendline } from '../../lib/charts/trendCalculations';
import {
  commonChartOptions,
  getCommonXAxis,
  getVolumeYAxis,
  getCommonTooltip,
  createTrendlineSeries,
  createUpperBandSeries,
  createLowerBandSeries,
  linePlotOptions,
  columnPlotOptions,
  areaPlotOptions,
  generateCorrectMonthLabels,
} from '../../lib/charts/chartConfig';
import TrendChartViewToggle, { type TrendChartView } from './TrendChartViewToggle';

const COMPONENT_COLORS = {
  RBC: '#b32017',
  Plasma: '#f59e0b',
  Platelets: '#00467f',
};

interface MonthlyDiscardTrendsProps {
  startDate?: string;
  endDate?: string;
  year?: number;
}

export default function MonthlyDiscardTrends({
  startDate,
  endDate,
  year,
}: MonthlyDiscardTrendsProps) {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<TrendChartView>('dual-axis');

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await chartApi.getDiscardTrends({ startDate, endDate, year });
        if (response.success) {
          setData(response.data);
          setSummary(response.summary);
        } else {
          setError(response.error || 'Failed to fetch data');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch chart data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [startDate, endDate, year]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-gray-500">Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-gray-500">No data available for this date range</div>
        <p className="text-sm text-gray-400 mt-2">Try Prior Fiscal Year or another range.</p>
      </div>
    );
  }

  const discardedValues = data.map((d) => (d as any).discarded || 0);
  const discardRateValues = data.map((d) => (d as any).discardRate || 0);
  const categories = generateCorrectMonthLabels(data);
  const hasComponentData = data.some((d) => (d as any).byComponent && Object.keys((d as any).byComponent).length > 0);
  const rbcValues = hasComponentData ? data.map((d) => ((d as any).byComponent as any)?.RBC || 0) : [];
  const plasmaValues = hasComponentData ? data.map((d) => ((d as any).byComponent as any)?.Plasma || 0) : [];
  const plateletsValues = hasComponentData ? data.map((d) => ((d as any).byComponent as any)?.Platelets || 0) : [];
  const volumeTrend = calculateTrendline(discardedValues);

  const renderTable = () => (
    <div className="overflow-x-auto h-full rounded-b-lg">
      <table className="w-full h-full divide-y divide-gray-200 rounded-b-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
            {hasComponentData && (
              <>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">RBC</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Plasma</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Platelets</th>
              </>
            )}
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Discarded</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Units</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Discard Rate</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((month, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">{month.label}</td>
              {hasComponentData && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {(((month as any).byComponent as any)?.RBC || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {(((month as any).byComponent as any)?.Plasma || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {(((month as any).byComponent as any)?.Platelets || 0).toLocaleString()}
                  </td>
                </>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {((month as any).discarded || 0).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {(month.total || 0).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                {((month as any).discardRate || 0).toFixed(1)}%
              </td>
            </tr>
          ))}
          {summary && (
            <tr className="bg-gray-50 border-t-2 border-gray-300">
              <td className="px-6 py-4 text-sm font-bold text-gray-900">Total</td>
              {hasComponentData && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {rbcValues.reduce((a, b) => a + b, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {plasmaValues.reduce((a, b) => a + b, 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {plateletsValues.reduce((a, b) => a + b, 0).toLocaleString()}
                  </td>
                </>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                {summary.totalDiscarded?.toLocaleString() || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                {summary.totalUnits?.toLocaleString() || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                {summary.overallDiscardRate?.toFixed(1) || 0}%
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderDualAxisChart = () => {
    const totalDiscardedValues = hasComponentData
      ? rbcValues.map((rbc, i) => rbc + plasmaValues[i] + plateletsValues[i])
      : discardedValues;
    const totalDiscardedTrend = calculateLinearTrendline(totalDiscardedValues);
    const series: any[] = [];

    if (hasComponentData) {
      series.push(
        { name: 'RBC', type: 'column', yAxis: 0, data: rbcValues, color: COMPONENT_COLORS.RBC, stacking: 'normal' },
        { name: 'Plasma', type: 'column', yAxis: 0, data: plasmaValues, color: COMPONENT_COLORS.Plasma, stacking: 'normal' },
        { name: 'Platelets', type: 'column', yAxis: 0, data: plateletsValues, color: COMPONENT_COLORS.Platelets, stacking: 'normal' }
      );
    } else {
      series.push({
        name: 'Discarded Units',
        type: 'column',
        yAxis: 0,
        data: discardedValues,
        color: '#ef4444',
        borderRadius: 3,
        pointPadding: 0.1,
        groupPadding: 0.2,
      });
    }

    series.push({
      name: 'Discard Rate',
      type: 'line',
      yAxis: 1,
      data: discardRateValues,
      color: '#7c3aed',
      lineWidth: 3,
      marker: { enabled: true, radius: 4, fillColor: '#7c3aed', lineColor: '#ffffff', lineWidth: 2 },
    });

    series.push(createTrendlineSeries(totalDiscardedTrend, '#00467f', 2));

    const options: Highcharts.Options = {
      chart: { type: 'line', backgroundColor: 'transparent', height: 350, spacing: [20, 20, 20, 20] },
      title: { text: undefined },
      xAxis: getCommonXAxis(categories),
      yAxis: [
        {
          title: { text: 'Units Discarded' },
          labels: {
            style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' },
            formatter: function () {
              const val = typeof this.value === 'number' ? this.value : 0;
              return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toString();
            },
          },
          gridLineColor: '#f1f5f9',
        },
        {
          title: { text: 'Discard Rate (%)' },
          opposite: true,
          labels: {
            style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' },
            formatter: function () {
              return `${this.value}%`;
            },
          },
          gridLineColor: '#f1f5f9',
        },
      ],
      tooltip: {
        ...getCommonTooltip((pointIndex) => data[pointIndex]?.label || '').tooltip,
        formatter: function (this: any) {
          const pointIndex = this.points?.[0]?.point?.index ?? this.points?.[0]?.x ?? 0;
          const monthName = data[pointIndex]?.label || this.x;
          let tooltip = `<b>${monthName}</b><br/>`;
          let totalDiscarded = 0;
          this.points?.forEach((point: any) => {
            if (point.series.type === 'column') {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toLocaleString()}</b><br/>`;
              totalDiscarded += point.y || 0;
            } else if (point.series.name === 'Discard Rate') {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toFixed(1)}%</b><br/>`;
            }
          });
          if (hasComponentData && totalDiscarded > 0) {
            tooltip += `<br/><b>Total Discarded: ${totalDiscarded.toLocaleString()}</b>`;
          }
          return tooltip;
        },
      },
      legend: { enabled: true, align: 'center', verticalAlign: 'bottom' },
      plotOptions: {
        column: {
          ...columnPlotOptions,
          stacking: hasComponentData ? 'normal' : undefined,
          color: hasComponentData ? undefined : '#ef4444',
        },
        line: {
          ...linePlotOptions,
          color: '#7c3aed',
        },
      },
      series,
      credits: { enabled: false },
    };

    return (
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  };

  const renderLineChart = () => {
    const linearTrend = calculateLinearTrendline(discardedValues);
    const series: any[] = [
      {
        name: 'Discarded Units',
        data: discardedValues,
        type: 'line',
        color: '#00467F',
        lineWidth: 3,
        marker: { enabled: true, radius: 4, fillColor: '#00467F', lineColor: '#ffffff', lineWidth: 2 },
      },
    ];

    series.push(
      createUpperBandSeries(volumeTrend.upperBand),
      createLowerBandSeries(volumeTrend.lowerBand),
      createTrendlineSeries(volumeTrend.trendline),
      createTrendlineSeries(linearTrend, '#00467f', 2)
    );

    const options: Highcharts.Options = {
      chart: { type: 'line', ...commonChartOptions },
      title: { text: undefined },
      xAxis: getCommonXAxis(categories),
      yAxis: getVolumeYAxis(),
      tooltip: {
        ...getCommonTooltip((pointIndex) => data[pointIndex]?.label || '').tooltip,
        formatter: function (this: any) {
          const pointIndex = (this as any).point?.index ?? (this as any).x ?? 0;
          const monthName = data[pointIndex]?.label || (this as any).key;
          let tooltip = `<b>${monthName}</b><br/>`;
          
          if (hasComponentData && data[pointIndex]) {
            const monthData = data[pointIndex] as any;
            const rbc = (monthData.byComponent as any)?.RBC || 0;
            const plasma = (monthData.byComponent as any)?.Plasma || 0;
            const platelets = (monthData.byComponent as any)?.Platelets || 0;
            const total = rbc + plasma + platelets;
            
            if (rbc > 0) {
              tooltip += `<span style="color:${COMPONENT_COLORS.RBC}">●</span> RBC: <b>${rbc.toLocaleString()}</b><br/>`;
            }
            if (plasma > 0) {
              tooltip += `<span style="color:${COMPONENT_COLORS.Plasma}">●</span> Plasma: <b>${plasma.toLocaleString()}</b><br/>`;
            }
            if (platelets > 0) {
              tooltip += `<span style="color:${COMPONENT_COLORS.Platelets}">●</span> Platelets: <b>${platelets.toLocaleString()}</b><br/>`;
            }
            if (total > 0) {
              tooltip += `<br/><b>Total: ${total.toLocaleString()}</b>`;
            }
          } else {
            tooltip += `<span style="color:${this.color}">●</span> <b>${this.y?.toLocaleString()}</b> units`;
          }
          
          return tooltip;
        },
      },
      legend: { enabled: false },
      plotOptions: {
        line: linePlotOptions,
      },
      series,
      credits: { enabled: false },
    };

    return (
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  };

  const renderStackedAreaChart = () => {
    if (!hasComponentData) {
      return (
        <div className="p-6 text-center text-gray-500">Component breakdown data not available</div>
      );
    }

    const totalDiscardedValues = rbcValues.map((rbc, i) => rbc + plasmaValues[i] + plateletsValues[i]);
    const totalDiscardedTrend = calculateLinearTrendline(totalDiscardedValues);
    const series: any[] = [
      { name: 'RBC', data: rbcValues, type: 'area', color: COMPONENT_COLORS.RBC, fillOpacity: 0.7, lineWidth: 2, marker: { enabled: false } },
      { name: 'Plasma', data: plasmaValues, type: 'area', color: COMPONENT_COLORS.Plasma, fillOpacity: 0.7, lineWidth: 2, marker: { enabled: false } },
      { name: 'Platelets', data: plateletsValues, type: 'area', color: COMPONENT_COLORS.Platelets, fillOpacity: 0.7, lineWidth: 2, marker: { enabled: false } },
    ];

    series.push(createTrendlineSeries(totalDiscardedTrend, '#00467f', 2));

    const options: Highcharts.Options = {
      chart: { type: 'area', backgroundColor: 'transparent', height: 350, spacing: [20, 20, 20, 20] },
      title: { text: undefined },
      xAxis: getCommonXAxis(categories),
      yAxis: {
        title: { text: undefined },
        labels: {
          style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' },
          formatter: function () {
            const val = typeof this.value === 'number' ? this.value : 0;
            return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toString();
          },
        },
        gridLineColor: '#f1f5f9',
      },
      tooltip: {
        ...getCommonTooltip((pointIndex) => data[pointIndex]?.label || '').tooltip,
        formatter: function (this: any) {
          const pointIndex = this.points?.[0]?.point?.index ?? this.points?.[0]?.x ?? 0;
          const monthName = data[pointIndex]?.label || this.x;
          let tooltip = `<b>${monthName}</b><br/>`;
          this.points?.forEach((point: any) => {
            tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toLocaleString()}</b><br/>`;
          });
          const total = this.points?.reduce((sum: number, point: any) => sum + (point.y || 0), 0) || 0;
          tooltip += `<br/><b>Total: ${total.toLocaleString()}</b>`;
          return tooltip;
        },
      },
      legend: { enabled: true, align: 'center', verticalAlign: 'bottom' },
      plotOptions: {
        area: areaPlotOptions,
      },
      series,
      credits: { enabled: false },
    };

    return (
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg break-inside-avoid mb-6">
      <div className="relative border-b border-gray-200">
        <TrendChartViewToggle activeView={activeView} onViewChange={setActiveView} />
        <div className="px-6 pb-4" style={{ paddingTop: '3rem', paddingRight: '10rem' }}>
          <h3 className="text-lg font-semibold text-gray-900">Monthly Discard Trends</h3>
          <p className="text-sm text-gray-600 mt-1">Track discarded units and discard rates over time</p>
        </div>
      </div>
      <div>
        {activeView === 'table' && renderTable()}
        {activeView === 'dual-axis' && renderDualAxisChart()}
        {activeView === 'line' && renderLineChart()}
        {activeView === 'stacked-area' && renderStackedAreaChart()}
      </div>
      {activeView !== 'table' && summary && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-700">Total Discarded:</span>
              <span className="text-sm font-bold text-gray-900 ml-2">{summary.totalDiscarded?.toLocaleString() || 0}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium text-gray-700">Overall Rate:</span>
              <span className="text-sm font-bold text-gray-900 ml-2">{summary.overallDiscardRate?.toFixed(1) || 0}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
